import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const EFFECTIVE_DATE = "August 21, 2026";

function Section({ title, children }) {
  return (
    <section className="mb-6">
      <h2 className="font-display font-semibold text-lg mb-2">{title}</h2>
      <div className="text-sm leading-relaxed text-muted-foreground space-y-2">{children}</div>
    </section>
  );
}

/** Public terms of service — required by app stores and OAuth consent screens. English only. */
export default function TermsPage() {
  return (
    <div className="min-h-screen bg-background pt-safe">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3 mb-6">
          <img src="/onsitecomms-icon.png" alt="OnSiteComms" className="h-10 w-10 rounded-lg object-contain" />
          <div>
            <h1 className="font-display font-bold text-2xl tracking-tight">
              <span className="text-[#2B3990]">OnSite</span><span className="text-[#C9A03B]">Comms</span> — Terms of Service
            </h1>
            <p className="text-xs text-muted-foreground">Effective date: {EFFECTIVE_DATE}</p>
          </div>
        </div>

        <Section title="1. Acceptance of these terms">
          <p>
            OnSiteComms ("the Service", "we", "us") is a private business communication platform for
            construction field teams, operated by Johnny Garbim. By creating an account or using the
            Service you agree to these Terms of Service. If you do not agree, do not use the Service.
            Website: <a className="text-[#2B3990] underline" href="https://on-site-comms.com">https://on-site-comms.com</a>.
          </p>
        </Section>

        <Section title="2. Eligibility and accounts">
          <ul className="list-disc pl-5 space-y-1">
            <li>The Service is a workplace tool intended for employees and contractors of participating companies. It is not directed at children under 16.</li>
            <li>Accounts are created or approved by a company administrator. You are responsible for keeping your credentials (password, passkeys, linked Apple or Google account) confidential.</li>
            <li>You must provide accurate information and notify us of any unauthorized use of your account.</li>
          </ul>
        </Section>

        <Section title="3. Acceptable use">
          <ul className="list-disc pl-5 space-y-1">
            <li>Use the Service only for legitimate business communication and job coordination.</li>
            <li>Do not upload unlawful, harassing, discriminatory or infringing content.</li>
            <li>Do not attempt to access other users' data, disrupt the Service, reverse engineer it, or bypass security measures.</li>
            <li>Company administrators may review content shared within their organization as business records.</li>
          </ul>
        </Section>

        <Section title="4. Your content">
          <p>
            You retain ownership of the messages, photos, videos, voice recordings, documents and other
            content you create. You grant us a limited license to store, process and transmit that content
            solely to operate the Service (including transcription and translation of voice messages
            processed by OpenAI's API). Content shared in company conversations may be retained as business
            records of the participating company.
          </p>
        </Section>

        <Section title="5. Privacy">
          <p>
            Our collection and use of personal information is described in our{" "}
            <Link className="text-[#2B3990] underline" to="/privacy">Privacy Policy</Link>. We do not sell
            your data, show ads, or use your data for cross-app tracking.
          </p>
        </Section>

        <Section title="6. Availability and changes">
          <p>
            We strive to keep the Service available but do not guarantee uninterrupted operation. We may
            modify, suspend or discontinue features at any time. We may update these terms; the effective
            date above will change when we do, and continued use constitutes acceptance.
          </p>
        </Section>

        <Section title="7. Termination">
          <p>
            You may stop using the Service and delete your account at any time inside the app
            (<strong>Profile → Delete my account</strong>). We or your company administrator may suspend or
            terminate accounts that violate these terms or when employment/contract with the participating
            company ends.
          </p>
        </Section>

        <Section title="8. Disclaimer and limitation of liability">
          <p>
            The Service is provided "as is" without warranties of any kind. To the maximum extent permitted
            by law, we are not liable for indirect, incidental or consequential damages, loss of data, or
            business interruption arising from use of the Service. The Service is a communication tool and
            does not replace professional judgment on job sites.
          </p>
        </Section>

        <Section title="9. Governing law and contact">
          <p>These terms are governed by the laws of the United States and the State of Florida, without regard to conflict-of-law rules.</p>
          <p>Questions: <a className="text-[#2B3990] underline" href="mailto:jlgarbim5@hotmail.com">jlgarbim5@hotmail.com</a></p>
        </Section>

        <div className="border-t pt-4 mt-8 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#2B3990] hover:underline" data-testid="terms-back-link">
            <ArrowLeft className="h-4 w-4" /> Back to OnSiteComms
          </Link>
          <span className="text-[11px] text-muted-foreground">Copyright © 2026 Johnny Garbim® — All rights reserved.</span>
        </div>
      </div>
    </div>
  );
}
